import dayjs from 'dayjs';
import { browser } from '$app/environment';
import lodash from 'lodash';
import * as stringFormat from 'string-format';
import mime from 'mime';
import { writable, type Writable } from 'svelte/store';

import DateString from '$shared/models/DateString';
import { isNonUTCDateString, isISODateString, isUtcDateString } from './date';
import { sentry } from './logger/sentry';
import { Currency } from '$provider/constants/Currency';
import { NoLabelKey, RequestOpenFormTypes } from '$provider/constants/global';
import UIErrorMessage from '$shared/constants/UIErrorMessage';
import { OtherExpection } from '$shared/exceptions/OtherExpection';
import { RegexValidator } from '$shared/constants/RegexValidator';
import { EntityParameterType } from '$shared/constants/EntityParameterType';
import { DateFormat } from '$provider/constants/Date';
import File from '$provider/models/File';
import type { CommonList } from '$shared/models/CommonList';
import { URLSearchParamsKey } from '$shared/constants/URLSearchParamsKey';

const format = (stringFormat.default || stringFormat) as (...args: any[]) => string;

export const isEmailValid = (email: string): boolean => {
  return RegexValidator.email.test(email);
};

export const getRandomString = (): string | undefined => {
  const temp = new Uint32Array(1);
  return browser ? window?.crypto?.getRandomValues(temp)[0].toString(16).split('.').pop() : '';
};

export const pluralize = (text: string, count = 1, prefix = ''): string => {
  return `${text}${count > 1 ? prefix : ''}${count > 1 ? 's' : ''}`.trim();
};

export const constructNavPathIndex = (navList: any[]): any => {
  const navPathIndex = {};
  const construct = (list) => {
    list.forEach((item) => {
      const route = item.key || item.data?.route;
      if (route) {
        navPathIndex[route] = item.id;
      }
      if (item.children) {
        construct(item.children);
      }
    });
  };
  construct(navList);
  return navPathIndex;
};

export const getFromObject = (key: string, obj: Record<any, any>): any => {
  try {
    return key?.split('.')?.reduce((o, i) => o && o[i], obj || {});
  } catch (err) {
    const exception = OtherExpection.from('Utils', 'getFromObject', err, {
      key,
      obj
    });
    sentry.log(exception);
    return '';
  }
};

export const middleEllipsis = (str: string, maxLength = 20): string => {
  if (!str) {
    return '';
  }
  if (str.length > maxLength) {
    return (
      str.slice(0, Math.floor(maxLength / 2)) +
      '...' +
      str.slice(str.length - Math.floor(maxLength / 2), str.length)
    );
  }
  return str;
};

export const formatSsnNumber = (ssn: string): string => {
  let value = ssn.replace(/\D/g, '');
  // add the first dash if number from the second group appear
  value = value.replace(/^(\d{3})(\d{1,2})/, '$1-$2');
  // add the second dash if numbers from the third group appear
  value = value.replace(/^(\d{3})-(\d{2})(.+?)/, '$1-$2-$3');
  return value;
};

export const parseDynamicData = (value: string | any): string => {
  try {
    // Convert date
    if (isUtcDateString(value) || isISODateString(value) || isNonUTCDateString(value)) {
      return new DateString(value).getParsedDate();
    }

    if (value instanceof DateString) {
      return value?.getParsedDate();
    }

    // Convert boolean
    if (typeof value === 'boolean') {
      return value ? 'Yes' : 'No';
    }
  } catch (err) {
    const exception = OtherExpection.from('Utils', 'parseDynamicData', err, {
      value
    });
    sentry.log(exception);
  }
  return value;
};

export const isUrl = (value: string): boolean => {
  let url;
  try {
    url = new URL(value);
  } catch (err) {
    return false;
  }
  return url?.protocol === 'http:' || url?.protocol === 'https:';
};

export const phoneNumberSeparator = (phoneNumber: string): string => {
  let value = phoneNumber;
  if (value.length === 3 || value.length === 7) {
    value += '-';
  }
  return value;
};

export const formatPhoneNumber = (phoneNumber: string): string => {
  return phoneNumber
    ?.trim()
    ?.split(/(\d{3})(\d{3})(\d{4})$/g)
    ?.filter(Boolean)
    ?.join('-');
};

export const getfileUploadPercentage = (completed: number, size: number): number => {
  return parseFloat(((completed / size) * 100).toFixed(2));
};

export const convertNumberToCurrencyString = (value: number | null): string => {
  return (
    value?.toLocaleString(Currency.Locale, { style: Currency.Style, currency: Currency.Type }) || ''
  );
};

export const parseProviderSearchKey = (
  searchKey: string
): { nameSearch: string | null; npiSearch: number | null } => {
  let nameSearch: string = searchKey;
  let npiSearch: number | null = null;
  if (searchKey && !isNaN(searchKey) && searchKey.length <= 10) {
    npiSearch = Number(searchKey);
    nameSearch = '';
  }
  return { nameSearch, npiSearch };
};

export const convertMillisecondsToMinute = (milliseconds: number): number => {
  return Math.floor(milliseconds / 60000);
};

export const convertMinutesToMillisecond = (minutes: number): number => {
  return minutes * 60 * 1000;
};

export const getObfuscatedString = (value: string, notObfuscatedCount = 4): string => {
  const midDotCode = '&#8226;';
  const regex = new RegExp(`(\\w)(?=(\\w| ){${notObfuscatedCount}})`, 'g');
  // Replace '-' special character to empty space.
  const label = value.replace(/-/g, ' ');
  // Replace char to mid dot except last 4 character.
  return label.replace(regex, midDotCode);
};

export const isNoLableKey = (key: string): boolean => {
  return key.indexOf(NoLabelKey) === 0;
};

export const isValidPath = (path: string): boolean => {
  // const regex = new RegExp(`(\\/[a-z_\\-\\s0-9\\.]+)+\\.`, 'g');
  // return regex.test(path);
  return !!path;
};

export const getFileNameFromPath = (path: string): string => {
  if (!isValidPath(path)) {
    throw new Error(UIErrorMessage.InvalidFilePath);
  }

  return File.getComputedFileNameByPath(path) || path?.split('/')?.pop() || '';
};

export const getDateIsExpired = (date: string): boolean => {
  if (!date) {
    return false;
  }
  // Check the expired date is actually expired or not
  return dayjs(date).isBefore(dayjs());
};

export const appendLabelCount = (label: string, list: CommonList[]): string => {
  const count: number = list.filter((item) => !!item.active).length;
  return count === 0 ? label : `${label} (${count})`;
};

export const attachNoLabel = (label: string): string => {
  if (!label) throw new Error('Param `label` is required to use this function');
  return `${NoLabelKey}${label}`;
};

export const openInNewTab = (path: string): void => {
  if (!path) throw new Error('`URL path` is missing, required to open in New tab');
  if (browser) {
    const url = `//${window.location.host}${path}`;
    window.open(url);
  }
};

// Replace the `+` symbol to `-`
export const cleanUpFileName = (fileName: string): string => {
  return fileName?.replace(/[+]/g, '-') || '';
};

export const removeNonASCIICharacters = (value: string): string => {
  return value?.replace(/[^\x00-\x7F]/g, '') || '';
};

export const getEnumAsCommonList = (
  enumObject: ObjectType<string>,
  mappedLabel: ObjectType<string> = {},
  mappedDesc: ObjectType<string> = {}
): CommonList[] => {
  return Object.values(enumObject).map((value) => ({
    id: value,
    label: mappedLabel[value] || value,
    desc: mappedDesc[value] || undefined
  }));
};

export const getDifference = (
  target: ObjectType<any> | undefined,
  record: ObjectType<any> | undefined
): ObjectType<any> => {
  if (!target || !record) return {};

  return Object.fromEntries(
    Object.entries(record).filter(
      ([key, value]) => key in record && !lodash.isEqual(target[key], value)
    )
  );
};

export const formatString = (message: string, ...args: any): string => {
  return format(message, args);
};

export const isJsonString = (text: string): boolean => {
  try {
    JSON.parse(text);
    return true;
  } catch {
    return false;
  }
};

export const getMimeType = (path: string): string | null => {
  return mime.getType(path);
};

// eslint-disable-next-line @typescript-eslint/ban-types
export const debounce = (callback: Function, delay: number): any => {
  let debounceTimer: ReturnType<typeof setTimeout>;
  return function (...args: any) {
    clearTimeout(debounceTimer);
    debounceTimer = setTimeout(() => callback(args), delay);
  };
};

export const getFormType = (param: EntityParameterType): string => {
  switch (param) {
    case EntityParameterType.ProviderLicense: {
      return RequestOpenFormTypes.NewLicense;
    }
    case EntityParameterType.ProviderNpi: {
      return RequestOpenFormTypes.NewNpi;
    }
    case EntityParameterType.ProviderEducation: {
      return RequestOpenFormTypes.NewEducation;
    }
    case EntityParameterType.ProviderBoardCertificationNumber:
    case EntityParameterType.ProviderNccpaBoardCertificateInfo: {
      return RequestOpenFormTypes.EditBoard;
    }
    default: {
      return RequestOpenFormTypes.BasicInfo;
    }
  }
};

export const getEmptyEntityProps = (credType: string): Record<'title' | 'actionLabel', string> => {
  return {
    title: `Add and Verify ${credType}`,
    actionLabel: `Add ${credType}`
  };
};

export const getEnumKeyByValue = (enumObject: ObjectType<string>, value: string) => {
  return Object.keys(enumObject)[Object.values(enumObject).indexOf(value)];
};

export const getTodayDate = (format?: string): string => {
  return dayjs().format(format || DateFormat.DatePickerFormat);
};

export const getNullableStore = (): Writable<null> => {
  return writable(null);
};

export const getFormatedFormTitle = (title: string, isEdit = false): string => {
  return `${isEdit ? 'Edit' : 'Add'} ${title}`;
};

export const getUrlSearchParam = (key: string): string => {
  const queryParam = new URLSearchParams(location.search);
  return queryParam.get(key) || '';
};

export async function withRetry<T>(
  fn: () => Promise<T | null>,
  retries = 1,
  delayMs = 1000
): Promise<T | null> {
  let lastError: any;

  for (let attempt = 0; attempt <= retries; attempt++) {
    try {
      const result = await fn();
      if (result) return result; // got a valid result
    } catch (err) {
      lastError = err;
    }

    if (attempt < retries) {
      await new Promise((res) => setTimeout(res, delayMs));
    }
  }

  throw lastError ?? new Error('Failed after retries');
}

export const getVerificationIdFromUrl = (): string => {
  return new URLSearchParams(location.search).get(URLSearchParamsKey.VerificationId) || '';
};

/**
 * Deletes the provided property from target if it exists, is a plain object (not array),
 * and has no own enumerable keys.
 */
export const deleteIfEmptyObjectProp = <T extends Record<string, any>>(
  target: T,
  key: keyof T
): void => {
  const value = target[key];
  if (!value || Array.isArray(value) || typeof value !== 'object') return;
  if (Object.keys(value).length === 0) {
    delete target[key];
  }
};

export const isDiffDate = (
  dateString: string | null | undefined,
  date: DateString | null | undefined
): boolean => {
  return (dateString ?? '') !== (date?.getDate() || '');
};
